// Step 8 — useOwlSubscription hook.
//
// Reads the user's Owl Hour subscription state from the
// owl_hour_subscriptions table via get_owl_subscription RPC.
//
// Falls back to phantom trial mode if the RPC is unavailable
// (table not yet created or RPC not deployed).
//
// Trust contract:
//   - Read via RLS-scoped RPC. No writes (trial start is separate).
//   - No localStorage. No external HTTP.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";

// ─── Types ─────────────────────────────────────────────────────

export interface OwlSubscriptionState {
  subscribed: boolean;
  trial: boolean;
  trialDaysLeft: number;
  plan: "standalone" | "addon" | "trial" | "none" | "expired" | "past_due" | "cancelled";
  status: string;
  trialAvailable: boolean;
  price: { standalone: number; addon: number; currency: string };
  loading: boolean;
  startTrial: () => Promise<boolean>;
  refetch: () => void;
}

const PRICE = { standalone: 180, addon: 100, currency: "GH₵" };

// ─── Hook ──────────────────────────────────────────────────────

export function useOwlSubscription(): OwlSubscriptionState {
  const { user } = useAuthSession();
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    if (!user) {
      setData(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const client = getBrowserClient();
    setLoading(true);

    void (async () => {
      try {
        const { data: rpcData, error: rpcErr } = await (client.rpc as unknown as (
          fn: string,
        ) => Promise<{
          data: Record<string, unknown> | null;
          error: { message: string } | null;
        }>)("get_owl_subscription");

        if (cancelled) return;

        if (rpcErr || !rpcData) {
          // RPC not available — fallback to phantom trial
          setData(null);
          setLoading(false);
          return;
        }

        setData(rpcData);
        setLoading(false);
      } catch {
        if (cancelled) return;
        setData(null);
        setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, tick]);

  const startTrial = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    const client = getBrowserClient();

    try {
      const response = await (client.rpc as unknown as (
        fn: string,
      ) => Promise<{
        data: Record<string, unknown> | null;
        error: { message: string } | null;
      }>)("start_owl_trial");
      if (response.error) return false;
    } catch {
      return false;
    }

    // Always refetch — the trial may have been created now or already existed
    refetch();
    return true;
  }, [user, refetch]);

  return useMemo(() => {
    // No user — not subscribed
    if (!user) {
      return {
        subscribed: false,
        trial: false,
        trialDaysLeft: 0,
        plan: "none" as const,
        status: "none",
        trialAvailable: true,
        price: PRICE,
        loading: false,
        startTrial,
        refetch,
      };
    }

    // RPC not available — show trial-available gate, don't grant access
    if (!data) {
      return {
        subscribed: false,
        trial: false,
        trialDaysLeft: 0,
        plan: "none" as const,
        status: "none",
        trialAvailable: true,
        price: PRICE,
        loading,
        startTrial,
        refetch,
      };
    }

    // Parse RPC response
    const subscribed = data.subscribed === true;
    const status = String(data.status ?? "none");
    const daysLeft = Number(data.days_left ?? 0);
    const trialAvailable = data.trial_available === true;
    const isAddon = data.is_addon === true;

    let plan: OwlSubscriptionState["plan"];
    if (status === "trial") plan = "trial";
    else if (status === "active" && isAddon) plan = "addon";
    else if (status === "active") plan = "standalone";
    else if (status === "expired") plan = "expired";
    else if (status === "past_due") plan = "past_due";
    else if (status === "cancelled") plan = "cancelled";
    else plan = "none";

    return {
      subscribed,
      trial: status === "trial",
      trialDaysLeft: status === "trial" ? daysLeft : 0,
      plan,
      status,
      trialAvailable,
      price: PRICE,
      loading,
      startTrial,
      refetch,
    };
  }, [user, data, loading, startTrial, refetch]);
}
